/**
 * Global Error Handler
 * Catches unhandled JavaScript errors and promise rejections
 * Ensures bug report dialog remains accessible
 */

interface ErrorInfo {
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  error?: Error;
  timestamp: string;
  url: string;
  userAgent: string;
}

class GlobalErrorHandler {
  private errorQueue: ErrorInfo[] = [];
  private maxErrors = 50;
  private isInitialized = false;
  private bugReportTrigger: (() => void) | null = null;

  /**
   * Initialize global error handlers
   */
  initialize() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Handle unhandled errors
    window.addEventListener('error', (event) => {
      this.handleError({
        message: event.message || 'Unknown error',
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;
      this.handleError({
        message:
          error?.message ||
          (typeof error === 'string' ? error : 'Unhandled promise rejection'),
        stack: error?.stack,
        error: error instanceof Error ? error : new Error(String(error)),
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    });

    // Prevent default error handling from breaking the UI
    window.addEventListener('error', () => {
      // Don't prevent default - let it log to console
      // But ensure our handler runs first
    });

    console.log('Global error handler initialized');
  }

  /**
   * Handle an error
   */
  private handleError(errorInfo: ErrorInfo) {
    // Add to error queue
    this.errorQueue.push(errorInfo);

    // Keep only recent errors
    if (this.errorQueue.length > this.maxErrors) {
      this.errorQueue.shift();
    }

    // Log error
    console.error('Global error caught:', errorInfo);

    // Store in window for bug report access
    if (typeof window !== 'undefined') {
      (window as any).__globalErrors = this.errorQueue;
    }

    // Show a visible error indicator if error boundary hasn't caught it
    // This ensures users can see something even if React error boundary fails
    this.showGlobalErrorUI(errorInfo);
  }

  /**
   * Show a global error UI overlay
   * This is a fallback if React error boundary doesn't catch the error
   */
  private showGlobalErrorUI(errorInfo: ErrorInfo) {
    if (typeof document === 'undefined') return;

    // Check if error boundary UI is already showing
    const existingErrorUI = document.getElementById('global-error-ui');
    if (existingErrorUI) return;

    // Create error UI overlay
    const errorOverlay = document.createElement('div');
    errorOverlay.id = 'global-error-ui';
    errorOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      font-family: system-ui, -apple-system, sans-serif;
      pointer-events: auto;
    `;

    // Escape HTML to prevent XSS
    const escapeHtml = (text: string) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    const errorMessage = escapeHtml(errorInfo.message || 'Unknown error');
    const errorStack = errorInfo.stack ? escapeHtml(errorInfo.stack) : '';

    errorOverlay.innerHTML = `
      <div style="background: white; border-radius: 8px; padding: 24px; max-width: 600px; width: 100%; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        <h2 style="color: #dc2626; font-size: 20px; font-weight: 600; margin-bottom: 12px;">
          Application Error
        </h2>
        <p style="color: #374151; margin-bottom: 16px;">
          The application encountered an error. You can report this issue or try to recover.
        </p>
        <div style="background: #f3f4f6; padding: 12px; border-radius: 4px; margin-bottom: 16px; font-size: 12px; font-family: monospace; max-height: 200px; overflow: auto; white-space: pre-wrap; word-break: break-all;">
          ${errorMessage}${errorStack ? '\n\n' + errorStack : ''}
        </div>
        <div style="display: flex; gap: 12px; flex-wrap: wrap;">
          <button id="error-report-bug-btn" style="flex: 1; min-width: 120px; padding: 10px; background: #dc2626; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 500;">
            Report Bug
          </button>
          <button id="error-reload-btn" style="flex: 1; min-width: 120px; padding: 10px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 500;">
            Reload Page
          </button>
          <button id="error-dismiss-btn" style="flex: 1; min-width: 120px; padding: 10px; background: #6b7280; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 500;">
            Dismiss
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(errorOverlay);

    // Add right-click context menu support
    errorOverlay.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.showBugReportForm(errorInfo, errorOverlay);
    });

    // Add event listeners
    const reportBugBtn = errorOverlay.querySelector('#error-report-bug-btn');
    const reloadBtn = errorOverlay.querySelector('#error-reload-btn');
    const dismissBtn = errorOverlay.querySelector('#error-dismiss-btn');

    if (reportBugBtn) {
      reportBugBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('[GlobalErrorHandler] Report Bug button clicked');
        this.showBugReportForm(errorInfo, errorOverlay);
      });
    }

    if (reloadBtn) {
      reloadBtn.addEventListener('click', () => {
        window.location.reload();
      });
    }

    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        errorOverlay.remove();
      });
    }
  }

  /**
   * Show bug report form - uses existing React BugReportDialog
   */
  private showBugReportForm(
    _errorInfo: ErrorInfo,
    _parentOverlay: HTMLElement
  ) {
    // Try to use React BugReportDialog if available
    this.tryReactBugReportDialog();
  }

  /**
   * Try to trigger React BugReportDialog if React is available
   */
  private tryReactBugReportDialog(): boolean {
    try {
      // Check if React is available and BugReportDialog can be triggered
      if (typeof window !== 'undefined') {
        const trigger = (window as any).__triggerBugReport;
        console.log(
          '[GlobalErrorHandler] Attempting to trigger BugReportDialog...',
          {
            triggerExists: !!trigger,
            triggerType: typeof trigger,
          }
        );

        if (trigger && typeof trigger === 'function') {
          console.log('[GlobalErrorHandler] Calling trigger function...');
          trigger();
          // Also dispatch custom event as backup
          window.dispatchEvent(new CustomEvent('triggerBugReport'));
          return true;
        } else {
          console.warn(
            '[GlobalErrorHandler] BugReportDialog trigger not available. React may not be mounted yet.'
          );
          // Try to dispatch a custom event as fallback
          const event = new CustomEvent('triggerBugReport');
          window.dispatchEvent(event);
          console.log(
            '[GlobalErrorHandler] Dispatched custom event as fallback'
          );
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error(
        '[GlobalErrorHandler] Error triggering BugReportDialog:',
        error
      );
      return false;
    }
  }

  /**
   * Get all captured errors
   */
  getErrors(): ErrorInfo[] {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrors() {
    this.errorQueue = [];
    if (typeof window !== 'undefined') {
      (window as any).__globalErrors = [];
    }
  }

  /**
   * Set callback to trigger bug report dialog
   */
  setBugReportTrigger(trigger: () => void) {
    this.bugReportTrigger = trigger;
  }

  /**
   * Trigger bug report dialog
   */
  triggerBugReport() {
    if (this.bugReportTrigger) {
      this.bugReportTrigger();
    }
  }
}

// Export singleton instance
export const globalErrorHandler = new GlobalErrorHandler();

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  // Initialize after a small delay to ensure DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      globalErrorHandler.initialize();
    });
  } else {
    globalErrorHandler.initialize();
  }
}
